// Shared helpers for the /webleads dashboard (V3).
//
// Visibility rules implemented by the page:
//   - Admin, Web Lead Manager, Executive Team  -> every location + overview board
//   - Area Manager (Eastern/Western)           -> branches within their region
//   - Everyone else (incl. General Manager)    -> their own branch only
// Workflow: the Web Lead Manager assigns each lead to a manager, and that
// manager assigns salespeople from their own team.
//   - Admin, Web Lead Manager                  -> assign managers (+ any salesperson)
//   - General Manager / Area Manager           -> assign salespeople on their own team
//   - Executive Team (with no assigner role)   -> monitoring only; leads are read-only

export const BRANCHES = [
  "Granger",
  "Mishawaka",
  "Elkhart",
  "Coloma",
  "Fort Wayne - Store",
  "Three Oaks",
  "Edgerton",
  "Kendallville",
  "Cromwell",
  "Delton",
  "LaPorte",
  "Roseland",
  "Adrian",
  "Shipshewana",
  "Eau Claire",
  "Schoolcraft",
];

export const EASTERN_BRANCHES = [
  "Adrian",
  "Edgerton",
  "Fort Wayne - Store",
  "Kendallville",
  "Cromwell",
  "Delton",
  "Schoolcraft",
  "Shipshewana",
];

export const WESTERN_BRANCHES = [
  "Roseland",
  "Elkhart",
  "Three Oaks",
  "Coloma",
  "Mishawaka",
  "Eau Claire",
  "Granger",
  "LaPorte",
];

// Branch values that are really regions/rollups, not physical locations.
const NON_BRANCH_VALUES = ["Eastern", "Western", "Corporate", "All Locations"];

export const derivePermissions = (roleList = [], user = {}) => {
  const has = (r) => roleList.includes(r);

  const seesAllLocations =
    has("Admin") || has("Web Lead Manager") || has("Executive Team");

  // The two area managers keep their historical Eastern/Western scope.
  const region =
    user.name === "Ben Chambers"
      ? "Eastern"
      : user.name === "Scott Foley"
      ? "Western"
      : has("Area Manager")
      ? user.branch // area manager without a mapped region falls back to branch
      : null;

  const viewableBranches = seesAllLocations
    ? BRANCHES
    : region === "Eastern"
    ? EASTERN_BRANCHES
    : region === "Western"
    ? WESTERN_BRANCHES
    : [user.branch].filter(Boolean);

  const canAssignManagers = has("Admin") || has("Web Lead Manager");
  const canAssignSales =
    canAssignManagers || has("General Manager") || has("Area Manager");

  return {
    seesAllLocations,
    canAssignManagers,
    canAssignSales,
    // Executive Team without an assigner role monitors; they never edit.
    canEditLeads: !(has("Executive Team") && !canAssignSales),
    canSeeDeleted:
      seesAllLocations || has("General Manager") || has("Area Manager"),
    // Execs can watch the company-wide queue, but only Admin/WLM work it.
    seesUnassignedQueue: canAssignManagers || has("Executive Team"),
    worksUnassignedQueue: canAssignManagers,
    isManagerish:
      seesAllLocations ||
      has("General Manager") ||
      has("Area Manager") ||
      has("Manager"),
    region,
    viewableBranches,
  };
};

// ---------------------------------------------------------------- fetching

// slim=1 keeps the heavy logs array server-side (docs carry a logCount
// instead; fetchLeadLogs pulls the log on demand). `since` caps the window
// for the all-time Completed/Deleted lists.
export const fetchLeads = async (type, branch, { since } = {}) => {
  const params = new URLSearchParams({ type, slim: "1" });
  if (branch && branch !== "All Locations") params.set("branch", branch);
  else params.set("user", "ALL");
  if (since) params.set("since", since.toISOString());
  const response = await fetch(`/api/leads/v2/getLeads?${params.toString()}`);
  if (!response.ok) throw new Error(`Failed to load ${type} leads`);
  return response.json();
};

export const fetchLeadLogs = async (id) => {
  const response = await fetch(`/api/leads/v2/getLogs?id=${id}`);
  if (!response.ok) throw new Error("Failed to load lead activity");
  return response.json();
};

export const monthsAgo = (months) => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return date;
};

const post = async (path, body) => {
  const response = await fetch(`/api/leads/v2/${path}`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error("Request failed");
  return response.json();
};

export const postProgress = (payload) => post("updateProgress", payload);
export const postNote = (payload) => post("addNote", payload);
export const postAssignManager = (payload) => post("assignManager", payload);
export const postAssignSales = (payload) => post("assignSales", payload);
export const postRemoveSales = (payload) => post("removeSales", payload);
export const postRemoveManager = (payload) => post("removeManager", payload);

// Same job-title screen the legacy pages used for weblead-eligible sales staff.
const SALES_JOB_TITLES = [
  "Inside Sales",
  "Outside Sales",
  "Kitchen Department Coordinator",
  "Cabinet Sales",
  "Installed Sales",
  "Installed Service Tech",
  "Estimating",
  "General Manager",
];

// Scott and James take web leads for any location, so every manager's assign
// dropdown offers them even though they're based in Roseland.
export const ASSIGNABLE_FROM_ANY_BRANCH = ["SCOTT BUNKELMAN", "JAMES TAYLOR"];

// These people aren't in the getsalesreps directory, so add them by hand
// (same pattern as the hand-added rows in getManagers). Guarded below so they
// don't appear twice if the cloud API starts returning them.
const MANUAL_SALES_DIRECTORY = [
  {
    UserID: 726,
    SalesRepCode: null,
    Name: "JAMES TAYLOR",
    EmailAddress: "james.taylor@bigclumber.com",
    JobTitle: null,
    Mobile: null,
    Branch: "Roseland",
  },
  {
    UserID: 315,
    SalesRepCode: null,
    Name: "LISA LASKOWSKI",
    EmailAddress: "lisal@bigclumber.com",
    JobTitle: null,
    Mobile: null,
    Branch: "Granger",
  },
  {
    UserID: 470,
    SalesRepCode: null,
    Name: "BRIDGET PATZER",
    EmailAddress: "bridget.patzer@bigclumber.com",
    JobTitle: null,
    Mobile: null,
    Branch: "Granger",
  },
];

export const fetchSalesDirectory = async () => {
  const response = await fetch("/api/getUserID");
  if (!response.ok) throw new Error("Failed to load directory");
  const data = await response.json();
  const directory = (data.recordset || []).filter(
    (person) =>
      SALES_JOB_TITLES.includes(person.JobTitle) ||
      person.Name === "BLAKE UPHAM" ||
      person.Name === "SCOTT BUNKELMAN" ||
      person.Name === "ASHLEY SMITTENDORF" ||
      person.Name === "JAMES TAYLOR"
  );
  MANUAL_SALES_DIRECTORY.forEach((person) => {
    if (
      !directory.some(
        (row) =>
          (row.EmailAddress || "").toLowerCase() ===
          person.EmailAddress.toLowerCase()
      )
    ) {
      directory.push(person);
    }
  });
  return directory;
};

// ------------------------------------------------------------ lead helpers

export const leadBranch = (lead) => {
  const managerBranch = lead.assignInfo?.assignedManager?.Branch;
  const salesBranch = (lead.assignInfo?.assignedSales || []).map(
    (s) => s?.Branch
  );
  if (managerBranch && !NON_BRANCH_VALUES.includes(managerBranch))
    return managerBranch;
  const realSalesBranch = salesBranch.find(
    (b) => b && !NON_BRANCH_VALUES.includes(b)
  );
  return realSalesBranch || managerBranch || salesBranch[0] || null;
};

// Mirrors the server's branch filter: manager OR any assigned salesperson.
export const leadMatchesBranch = (lead, branch) =>
  lead.assignInfo?.assignedManager?.Branch === branch ||
  (lead.assignInfo?.assignedSales || []).some((s) => s?.Branch === branch) ||
  leadBranch(lead) === branch;

export const leadMatchesPerson = (lead, userID) => {
  const id = parseInt(userID, 10);
  return (
    lead.assignInfo?.assignedManager?.UserID === id ||
    (lead.assignInfo?.assignedSales || []).some((s) => s?.UserID === id)
  );
};

export const wasContacted = (lead) =>
  Boolean(
    lead.leadProgress?.contactStatus?.phoneContact?.status ||
      lead.leadProgress?.contactStatus?.emailContact?.status
  );

export const needsSalesperson = (lead) =>
  Boolean(lead.assignInfo?.managerAssigned) &&
  (lead.assignInfo?.assignedSales || []).length === 0;

export const searchLead = (lead, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return Object.values(lead.webleadEntry || {}).some(
    (value) => typeof value === "string" && value.toLowerCase().includes(q)
  );
};

export const leadStatus = (lead, tab) => {
  if (tab === "Deleted") return { label: "Deleted", tone: "red" };
  if (tab === "Completed") {
    return lead.saleData?.orderNumber
      ? { label: `Won · Order ${lead.saleData.orderNumber}`, tone: "emerald" }
      : { label: "Completed", tone: "emerald" };
  }
  if (!lead.assignInfo?.managerAssigned)
    return { label: "Unassigned", tone: "slate" };
  if (!wasContacted(lead)) return { label: "Awaiting contact", tone: "red" };
  if (lead.saleData?.orderNumber)
    return { label: "Order placed", tone: "emerald" };
  if (lead.saleData?.quoteNumber) return { label: "Quoted", tone: "sky" };
  if (needsSalesperson(lead)) return { label: "Needs salesperson", tone: "amber" };
  return { label: "In progress", tone: "sky" };
};

// Translucent tints so the chips sit on any card surface (white, slate-800 or
// Liquid Glass). The 700-weight text keeps contrast on the lighter glass
// panels; each tone carries a lighter dark-mode pair.
export const STATUS_TONES = {
  red: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/25",
  amber: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/25",
  emerald:
    "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/25",
  sky: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/25",
  slate: "bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/25",
};

// Left accent stripe on lead cards, matching the status badge tone. A filled
// stripe rather than a colored border-left: .app-card sets border-color after
// Tailwind's utilities, so a `border-l-*` color on the card itself never shows.
export const STATUS_STRIPES = {
  red: "bg-red-500",
  amber: "bg-amber-500",
  emerald: "bg-emerald-500",
  sky: "bg-sky-500",
  slate: "bg-slate-400 dark:bg-slate-500",
};

// Legacy border-left variant of the same accent (kept for compatibility).
export const STATUS_BORDERS = {
  red: "border-l-red-500",
  amber: "border-l-amber-500",
  emerald: "border-l-emerald-500",
  sky: "border-l-sky-500",
  slate: "border-l-slate-400",
};

// Metadata chips render as a neutral pill with a colored dot so the text
// stays readable on both light and dark card surfaces.
export const chipDotColor = (label) => {
  switch (label) {
    case "Quick Contact":
      return "bg-blue-500";
    case "Search Engine":
      return "bg-fuchsia-500";
    case "Social (Facebook, Instagram, LinkedIn)":
      return "bg-orange-500";
    case "Radio Advertisement":
      return "bg-yellow-500";
    case "TV Advertisement":
      return "bg-green-500";
    case "Friend":
      return "bg-purple-500";
    case "News":
      return "bg-amber-500";
    case "Lumber / Building Materials":
      return "bg-fuchsia-400";
    case "Deck Materials":
      return "bg-orange-400";
    case "Insulation":
      return "bg-yellow-400";
    case "Doors / Windows":
      return "bg-green-400";
    case "Siding / Roofing":
      return "bg-purple-400";
    case "Cabinetry":
      return "bg-slate-400";
    case "Post Frame Buildings":
      return "bg-pink-400";
    case "Trusses / Panels":
      return "bg-rose-400";
    case "Other":
      return "bg-cyan-500";
    default:
      return "bg-slate-400";
  }
};

const AVATAR_COLORS = [
  "bg-sky-600",
  "bg-emerald-600",
  "bg-violet-600",
  "bg-rose-600",
  "bg-amber-600",
  "bg-teal-600",
  "bg-indigo-600",
  "bg-orange-600",
];

export const avatarColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

export const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

// "TAYLOR SMITH" -> "Taylor Smith" for directory records.
export const titleCase = (name = "") =>
  name
    .toLowerCase()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const daysSince = (date) => {
  const then = new Date(date).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / 86400000);
};

export const ageBadge = (date) => {
  const days = daysSince(date);
  if (days === null) return null;
  if (days <= 0) return { label: "New today", tone: "emerald" };
  if (days === 1) return { label: "1 day old", tone: "emerald" };
  if (days <= 7) return { label: `${days} days old`, tone: "slate" };
  return { label: `${days} days old`, tone: "amber" };
};

export const isSameMonth = (date, now = new Date()) => {
  const d = new Date(date);
  return (
    d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
  );
};

// -------------------------------------------------------------- CSV export

const csvCell = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value);
  // Excel treats a leading =/+/-/@ as a formula; quote-prefix those cells.
  const safe = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

const csvDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// The card badge reads "Won · Order 1234"; the CSV keeps the outcome and the
// order number in separate columns.
const csvStatus = (lead, tab) =>
  tab === "Completed"
    ? lead.saleData?.orderNumber
      ? "Won"
      : "Completed"
    : leadStatus(lead, tab).label;

const LEAD_CSV_HEADERS = [
  "List",
  "Status",
  "Entry Date",
  "Age (days)",
  "Customer",
  "Email",
  "Phone",
  "Address",
  "City",
  "State",
  "Zip",
  "Form Type",
  "Working On",
  "Heard About",
  "Looking For",
  "Branch",
  "Manager",
  "Manager Assigned",
  "Salespeople",
  "Phone Contacted",
  "Phone Contact Date",
  "Email Contacted",
  "Email Contact Date",
  "Quote Number",
  "Order Number",
  "Completed Date",
  "Deleted Date",
  "Last Updated",
  "Notes",
  "Latest Note",
];

const leadCsvRow = (lead, tab) => {
  const entry = lead.webleadEntry || {};
  const contact = lead.leadProgress?.contactStatus || {};
  const status = lead.leadProgress?.leadStatus || {};
  const sale = lead.saleData || {};
  const manager = lead.assignInfo?.assignedManager || {};
  const sales = lead.assignInfo?.assignedSales || [];
  const notes = lead.notes || [];
  const latest = notes[notes.length - 1];

  return [
    tab,
    csvStatus(lead, tab),
    csvDate(entry.entryDate),
    daysSince(entry.entryDate),
    entry.customerName,
    entry.customerEmail,
    entry.customerPhone,
    entry.customerAddress,
    entry.customerCity,
    entry.customerState,
    entry.customerZip,
    entry.type,
    entry.customerWorkingOn,
    entry.hearAbout === "Other" && entry.hearOther
      ? `Other — ${entry.hearOther}`
      : entry.hearAbout,
    entry.customerLookingFor,
    leadBranch(lead),
    manager.Name ? titleCase(manager.Name) : "",
    csvDate(lead.assignInfo?.managerAssignDate),
    sales
      .map((s) => (s?.Name ? titleCase(s.Name) : ""))
      .filter(Boolean)
      .join("; "),
    contact.phoneContact?.status ? "Yes" : "No",
    csvDate(contact.phoneContact?.date),
    contact.emailContact?.status ? "Yes" : "No",
    csvDate(contact.emailContact?.date),
    sale.quoteNumber,
    sale.orderNumber,
    csvDate(status.completed?.date),
    csvDate(status.deleted?.date),
    csvDate(status.lastUpdated),
    notes.length,
    latest ? `${latest.username || "Unknown"}: ${latest.note || ""}` : "",
  ];
};

// groups: { Active: [...], Completed: [...], ... } — one row per lead, tagged
// with the list it came from.
export const buildLeadsCsv = (groups) => {
  const rows = Object.entries(groups).flatMap(([tab, list]) =>
    (list || []).map((lead) => leadCsvRow(lead, tab))
  );
  return [LEAD_CSV_HEADERS, ...rows]
    .map((row) => row.map(csvCell).join(","))
    .join("\r\n");
};

export const downloadCsv = (filename, contents) => {
  // Leading BOM so Excel opens the file as UTF-8.
  const blob = new Blob([`\uFEFF${contents}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
